/**
 * "Bundle works better in the desktop app" (desktop browsers) or the app-store style prompt (phone browsers), above
 * the shell. Which one, and whether at all, is GetApp.promptFor (packages/core); hidden in the desktop app, in an
 * installed PWA, and for 30 days after × or Download. It shares the one top slot with the connection banner and
 * "Bundle is in beta" (platform/topBanner.ts: offline > beta > get-app).
 */
import { GetApp } from "@bundle/core";
import { AppBanner, Banner, useT } from "@bundle/ui";
import { useEffect, useMemo, useState } from "react";
import { isDesktop } from "../platform/desktop.ts";
import { useTopBanner } from "../platform/topBanner.ts";
import { currentPlatform, isStandalone, loadAvailability, openDownload, snoozeGetApp, syncSmartBanner, useGetAppState } from "../platform/getApp.ts";

/** Which get-app prompt this page wants (GetApp.promptFor over the browser's state); "none" when it wants none. */
function useGetAppPromptState(path: string) {
  const { availability, snoozedUntil } = useGetAppState();
  const platform = useMemo(() => currentPlatform(), []);
  const [nowMs] = useState(() => Date.now());
  useEffect(() => loadAvailability(), []);
  useEffect(() => syncSmartBanner(), [path, availability]);
  const prompt = GetApp.promptFor({ platform, inDesktopApp: isDesktop(), standalone: isStandalone(), snoozedUntil, nowMs, availability, path });
  return { ...prompt, platform };
}

export function GetAppPrompt({ path }: { path: string }) {
  const t = useT();
  const { kind, target, platform } = useGetAppPromptState(path);
  const shown = useTopBanner("getApp", kind !== "none" && target !== null);
  if (!shown || target === null || kind === "none") return null;
  const download = () => {
    snoozeGetApp();
    openDownload(target.url);
  };

  if (kind === "mobile") {
    const store = GetApp.storeLineKey(target.store);
    return (
      <AppBanner
        title={t("getApp.mobile.title")}
        message={t("getApp.mobile.message")}
        store={store === null ? "" : t(store)}
        action={{ label: t(GetApp.mobileActionKey(target.store)), onPress: download, testID: "get-app-mobile-action" }}
        onDismiss={() => snoozeGetApp()}
        dismissLabel={t("getApp.dismiss")}
        testID="get-app-mobile"
      />
    );
  }
  const label = GetApp.downloadLabelKey(platform);
  if (label === null) return null;
  return (
    <Banner
      tone="info"
      icon="monitor"
      placement="edge"
      message={t("getApp.banner.desktop")}
      secondaryAction={{ label: t("getApp.openInApp"), onPress: () => window.location.assign(GetApp.handoffUrl(path)), testID: "get-app-open" }}
      action={{ label: t(label), icon: "download", variant: "primary", onPress: download, testID: "get-app-download" }}
      onDismiss={() => snoozeGetApp()}
      dismissLabel={t("getApp.dismiss")}
      testID="get-app"
    />
  );
}
